import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@mui/styles";
import { Container, Paper } from "@mui/material";
import { debounce } from "lodash";

//... Date & Calendar imports
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import "./calendar.css";

//... Context Provider imports
import AppDataContext from "../../common/appContext";

//... Shared Component imports
import AlertSnackbar from "../../sharedComponents/Snackbar/AlertSnackbar";
import Loader from "../../sharedComponents/loader/loader";
import ConfirmationDialog from "../../sharedComponents/dialog/ConfirmationDialog";
import SlidingPaneComponent from "../../sharedComponents/SlidingPane/SlidingPane";

//... Service imports
import { PlayerProfileService } from "../../services/playerProfile.Service";
import { CalendarEventServices } from "../../services/CalendarEvents.Services";

//... Child Component imports
import EventForm from "./manageCalendarEvent/EventForm";
import SearchFilter from "./SearchFilter";
import CalendarEventListing from "./CalendarEventListing";
import CalendarEventHeaderBar from "./CalendarEventHeaderBar";
import EventWithPopover from "./EventWithPopover";
import Helper from "../../common/Helper";
import { useNavigate } from "react-router-dom";
import RouteConstants from "../../common/RouteConstants";

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  gridItem: {
    paddingTop: "10px",
  },
  calendarControl: {
    width: "100%",
    "& .MuiInputBase-input": {
      padding: theme.spacing(1, 0.5, 1, 1),
    },
    "& .MuiFormLabel-root": {
      transform: "translate(14px, 8px) scale(1)",
    },
    "& .MuiInputLabel-shrink.MuiFormLabel-filled": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
    "& .MuiInputAdornment-root": {
      marginLeft: theme.spacing(0.5),
      "& .MuiButtonBase-root": {
        padding: theme.spacing(0.5),
        "& .MuiSvgIcon-root": {
          fontSize: "1.1rem",
        },
      },
    },
  },
  card: {
    overflow: "visible",
    maxWidth: "100%",
    flex: "0 0 auto",
    margin: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(0.2),
      marginRight: theme.spacing(0.2),
    },
  },
  cardcontent: {
    padding: theme.spacing(0.5, 0),
    "&:last-child": {
      paddingBottom: theme.spacing(0) + "!important",
    },
  },
  boxContent: {
    marginLeft: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.only("sm")]: {
      marginLeft: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(2),
      "& span": {
        fontSize: "10px",
      },
      alignItems: "center",
    },
  },
  logoContent: {
    display: "flex",
    alignItems: "center",
  },
  gameContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  NBABackground: {
    background: "#e0e7fa",
  },
  highlightCard: {
    border: "red solid 2px",
  },
}));

let gridData = [];
const initialStartDate = moment().startOf("month");
const initialEndDate = moment().endOf("month");

const EventScheduleContainer = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const { user, commonEventList } = useContext(AppDataContext);

  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(initialStartDate);
  const [toDate, setToDate] = useState(initialEndDate);
  const [showCalendar, setShowCalendar] = useState(true);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  const [openDeleteEventDialog, setOpenDeleteEventDialog] = useState(false);
  const [selectedCalendarID, setSelectedCalendarID] = useState(null);

  //... Add/Edit Calendar Events
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState({
    eventId: 0,
    eventStartDateTime: moment(),
    eventEndDateTime: moment(),
    title: "",
    description: "",
    location: "",
    participantEventId: 0,
    publish: false,
  });
  const [participants, setParticipants] = useState([]);
  const [participantsOpen, setParticipantsOpen] = useState([]);
  const [showParticipants, setShowParticipants] = useState(true);
  const [participantList, setParticipantList] = useState([]);
  const [eventRosterID, setEventRosterID] = useState("09");
  const [playerData, setPlayerData] = useState([]);
  const [activeView, setActiveView] = useState("participants");
  const [playerNameSearchText, setPlayerNameSearchText] = useState("");
  const [editMode, setEditMode] = useState(false);

  //... Alert State holders.
  const [alert, setAlert] = useState({
    open: false,
    type: "warning",
    message: "",
  });

  //... Refs
  const calendarRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (commonEventList?.length > 0) {
      setPlayerData([]);
      setParticipantList([]);
      setEventRosterID("09");
      gridData = [];
      processEvents(commonEventList)
        .then(setParticipantList)
        .catch((err) => {
          console.error("Error fetching event data:", err);
          setParticipantList([]);
        });
    }
  }, [commonEventList]);

  useEffect(() => {
    if (
      !eventRosterID ||
      commonEventList.length === 0 ||
      participantList?.length === 0
    ) {
      return;
    }

    // Find the event using the eventRosterID
    const event = commonEventList.find(
      (event) => event.leagueId === eventRosterID
    );
    if (!event) {
      return;
    }

    // Find the participant list corresponding to the eventId
    const participant = participantList.find(
      (list) => list.eventId === event.eventId
    );
    if (!participant) {
      return;
    }

    // Extract and log player data
    const { data } = participant;
    gridData = [...data];
    setPlayerData(data || []);
  }, [eventRosterID, commonEventList, participantList]);

  useEffect(() => {
    if (Object.keys(user).length > 0 && fromDate && toDate) {
      const onLoadFetchEvents = async () => {
        try {
          const filtered = await fetchEventCalendarList(fromDate, toDate);
          setFilteredEvents(filtered);
        } catch (error) {
          console.error("Error fetching events first load:", error);
        }
      };

      onLoadFetchEvents();
    }
  }, [user]);

  //... Handlers
  const processEvents = async (events) => {
    if (!events?.length) return [];

    // Create an array of promises for API calls
    const resultArray = await Promise.all(
      events.map(async (event) => {
        const { eventId } = event;
        const playerData = await fetchPlayerListByEventID(eventId);
        return { eventId, data: playerData };
      })
    );

    return resultArray;
  };

  const toggleParticipants = () => {
    setShowParticipants((prev) => !prev);
  };

  const handleToggle = (view) => {
    setActiveView(view); // Toggle view
  };

  const addParticipant = (playerDetails) => {
    const isPlayerExist = participants.some(
      (player) => player.playerID === playerDetails.playerID
    );

    setParticipants(
      isPlayerExist
        ? participants.filter(
            (player) => player.playerID !== playerDetails.playerID
          )
        : [...participants, playerDetails]
    );
  };

  const handleSelectAllParticipant = (isChecked) => {
    setParticipants(isChecked ? playerData || [] : []);
  };

  const searchPlayerOrCollegeName = (searchValue) => {
    setPlayerNameSearchText(searchValue);
    if (searchValue) {
      const tempData = [...gridData];
      const updatedPlayerData = tempData.filter((elem) => {
        return (
          elem.playerName
            .toLowerCase()
            .includes(searchValue.toLowerCase().trim()) ||
          elem.collegeOrClub
            .toLowerCase()
            .includes(searchValue.toLowerCase().trim())
        );
      });
      setPlayerData(updatedPlayerData);
    } else {
      setPlayerData(gridData);
    }
  };

  const handleOnSaveAction = (calendarEventID) => {
    const { eventStartDateTime, eventEndDateTime, title } = eventDetails;

    //@ Check for missing fields
    if (!eventStartDateTime || !eventEndDateTime || !title?.trim()) {
      showAlert(
        "warning",
        `Please provide an Event ${
          !eventStartDateTime || !eventEndDateTime ? "Date" : "Title"
        }..`
      );
      return;
    }

    //@ Validate that eventEndDateTime is not before eventStartDateTime
    if (moment(eventEndDateTime).isBefore(moment(eventStartDateTime))) {
      showAlert("warning", "Event end time cannot be before event start time.");
      return;
    }

    const payload = {
      ...eventDetails,
      eventStartDateTime: Helper.formatEventDateTime(eventStartDateTime),
      eventEndDateTime: Helper.formatEventDateTime(eventEndDateTime),
      eventId: calendarEventID || 0,
      participants: participants.map((player) => player.playerID).join(", "),
      participantEventId:
        commonEventList.find((event) => event.leagueId === eventRosterID)
          ?.eventId || null,
      processedBy: user.loginID,
    };

    saveOrUpdateCalendarEventDetails(payload, Boolean(calendarEventID));
  };

  // Handle date range change and update calendar view
  const handleDateRangeChange = (startDate, endDate) => {
    setFromDate(startDate);
    setToDate(endDate);
  };

  const handleGoButtonClick = async () => {
    //Reset event if its there..
    resetSelectionOfEvent();

    // Check if the selected date range spans different months
    const isDifferentMonth = fromDate.month() !== toDate.month();

    setShowCalendar(!isDifferentMonth);

    if (!isDifferentMonth && calendarRef.current) {
      const calendarApi = await calendarRef.current.getApi();
      const startOfMonth = moment(fromDate).startOf("month");
      await calendarApi.gotoDate(startOfMonth.format("YYYY-MM-DD"));
    }

    const filtered = await fetchEventCalendarList(fromDate, toDate);
    setFilteredEvents(filtered);
  };

  // Update the date pickers whenever the FullCalendar view changes (month changes)
  const handleDatesSet = useCallback(
    async (arg) => {
      //Reset event if its there..
      resetSelectionOfEvent();

      const currentStart = moment(arg.view.currentStart).local();
      const startOfMonth = currentStart.clone().startOf("month").startOf("day");
      const endOfMonth = currentStart.clone().endOf("month").endOf("day");

      // Only proceed if the date range has actually changed
      const datesChanged =
        !startOfMonth.isSame(fromDate, "day") ||
        !endOfMonth.isSame(toDate, "day");

      if (datesChanged) {
        setFromDate(startOfMonth);
        setToDate(endOfMonth);

        const filtered = await fetchEventCalendarList(startOfMonth, endOfMonth);
        setFilteredEvents(filtered);
      }
    },
    [fromDate, toDate]
  );

  // Handle event click in the FullCalendar view
  const handleEventClick = (info) => {
    const clickedEvent = filteredEvents.find(
      (event) => event.eventId === parseInt(info.event.id)
    );
    setSelectedEvent([clickedEvent]);

    // Scroll to the event in the list view
    scrollToEventInList(clickedEvent);

    // Automatically expand the corresponding accordion
    setExpandedAccordions([clickedEvent.eventId]);
  };

  const handleDateClick = ({ dateStr }) => {
    const selectedDate = moment(dateStr).format("YYYY-MM-DD");

    const eventsForSelectedDay = filteredEvents.filter((event) =>
      event.eventStartDateTime.startsWith(selectedDate)
    );

    if (!eventsForSelectedDay.length) {
      setSelectedEvent([]);
      setExpandedAccordions([]);
      return;
    }

    const allEventIDs = eventsForSelectedDay.map(({ eventId }) => eventId);

    console.log(`Events on ${selectedDate}:`, eventsForSelectedDay);
    setSelectedEvent(eventsForSelectedDay);
    scrollToEventInList(eventsForSelectedDay[0]);
    setExpandedAccordions(allEventIDs);
  };

  // Scroll the event in the list view if it's not visible
  const scrollToEventInList = (event) => {
    if (listRef.current) {
      const eventElement = document.getElementById(`event-${event.eventId}`);
      if (eventElement) {
        // Scroll to the event smoothly
        eventElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        // Force reflow after scrolling
        setTimeout(() => {
          window.scrollBy(0, 1);
          window.scrollBy(0, -1);
        }, 300);
      }
    }
  };

  // Handle event card accordion expansion (toggle)
  const handleAccordionChange = (event) => {
    setExpandedAccordions((prevExpanded) => {
      if (prevExpanded.includes(event.eventId)) {
        setSelectedEvent([]);
        return prevExpanded.filter((eventId) => eventId !== event.eventId); // Collapse if already expanded
      } else {
        return [...prevExpanded, event.eventId]; // Expand if not already expanded
      }
    });
  };

  // Handle participant accordion expansion (toggle)
  const participantsCollapseChange = (event) => {
    setParticipantsOpen((prevExpanded) => {
      if (prevExpanded.includes(event.eventId)) {
        return prevExpanded.filter((eventId) => eventId !== event.eventId); // Collapse if already expanded
      } else {
        return [...prevExpanded, event.eventId]; // Expand if not already expanded
      }
    });
  };

  // Handle Expand All / Collapse All
  const handleExpand = (expand) => {
    if (expand) {
      // Expand all events
      setExpandedAccordions(filteredEvents.map((event) => event.eventId));
    } else {
      setSelectedEvent([]);
      // Collapse all events
      setExpandedAccordions([]);
    }
  };

  const editEventCalendarDetails = ({
    eventId = 0,
    eventStartDateTime,
    eventEndDateTime,
    title = "",
    description = "",
    location = "",
    publish = false,
    participants = "",
    participantEventId = null,
  }) => {
    const newParticipant = participantList.find(
      (list) => list.eventId === String(participantEventId)
    );

    const filteredPlayers = newParticipant?.data?.filter((player) =>
      participants
        .split(",")
        .map((participant) => participant.trim())
        .includes(String(player.playerID).trim())
    );

    setEventDetails({
      eventId,
      eventStartDateTime: eventStartDateTime
        ? moment(eventStartDateTime)
        : null,
      eventEndDateTime: eventEndDateTime ? moment(eventEndDateTime) : null,
      title,
      description,
      location,
      publish: Boolean(publish),
      participantEventId: participantEventId || null,
    });

    setEventRosterID(String(participantEventId).slice(4) || "09");
    setParticipants(filteredPlayers || []);
    setEditMode(true);
    setIsPanelOpen(true);
  };

  const executeEventDeleteAction = async (calendarEventID) => {
    setSelectedCalendarID(calendarEventID);
    setOpenDeleteEventDialog(true);
  };

  const onDeleteEventAgree = async () => {
    setOpenDeleteEventDialog(false);
    await deleteCalendarEvent(selectedCalendarID);
  };

  const handleChange = useCallback((field, value) => {
    setEventDetails((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDateChange = (date) =>
    updateEventDetail("eventStartDateTime", date);

  const handleEndDateChange = (date) =>
    updateEventDetail("eventEndDateTime", date);

  const updateEventDetail = (field, newDate) => {
    setEventDetails((prevState) => ({
      ...prevState,
      [field]: adjustDate(newDate),
    }));
  };

  const onPanelClose = () => {
    setIsPanelOpen(false);
    setEditMode(false);
    resetDependecies();
    resetSelectionOfEvent();
  };

  //... Utilities
  const showAlert = (type, message) => {
    setAlert({ open: true, type, message });
  };

  const resetDependecies = () => {
    setEventDetails({
      eventId: 0,
      eventStartDateTime: moment(),
      eventEndDateTime: moment(),
      title: "",
      description: "",
      location: "",
      publish: false,
    });
    setParticipants([]);
    setPlayerNameSearchText("");
    setPlayerData(gridData);
  };

  const resetSelectionOfEvent = () => {
    setSelectedEvent([]);
    setExpandedAccordions([]);
    setParticipantsOpen([]);
    setActiveView("participants");
  };

  const adjustDatesToMonth = (startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);

    // Check if both dates are in the same month and year
    if (start.isSame(end, "month") && start.isSame(end, "year")) {
      // If the dates are in the same month, return the 1st and last day of that month
      return {
        startOfMonth: start.startOf("month").format("YYYY-MM-DD HH:mm:ss"),
        endOfMonth: end.endOf("month").format("YYYY-MM-DD HH:mm:ss"),
      };
    }

    // If dates are not in the same month, return the original dates
    return {
      startOfMonth: start.format("YYYY-MM-DD HH:mm:ss"),
      endOfMonth: end.format("YYYY-MM-DD HH:mm:ss"),
    };
  };

  const adjustDate = (date) => {
    if (!date || !moment.isMoment(date)) return date;

    return date.hour() === 0 && date.minute() === 0
      ? date.clone().add(1, "day").startOf("day")
      : date;
  };

  const getRandomColor = () => {
    // const colors = ["#73BAFB", "#EFD6FF", "#CAFDF5", "#D3FCD2"];
    const colors = [
      "blue",
      "green",
      "red",
      "orange",
      "purple",
      "pink",
      "brown",
      "grey",
    ];

    // Select random icon and color
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    return { color: randomColor };
  };

  const viewPlayerProfile = (eventId, playerId) => {
    navigate(`/${eventId}${RouteConstants.VIEW_PLAYER_PROFILE}/${playerId}`);
  };

  //...APIS
  const fetchEventCalendarList = async (startDate, endDate) => {
    setLoading(true);
    try {
      const { startOfMonth, endOfMonth } = adjustDatesToMonth(
        startDate,
        endDate
      );
      const { data = [] } = await CalendarEventServices.getCalendarEventsData(
        startOfMonth,
        endOfMonth
      );

      //console.log("fetchEventCalendarList", user.isCompleteTeamUser);

      const eventsToShow = user.isCompleteTeamUser
        ? data.filter((event) => event.publish)
        : data;

      return eventsToShow.map((event) => ({
        ...event,
        icon: "🏀",
        color: getRandomColor().color,
      }));
    } catch (error) {
      console.error("Error fetching:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteCalendarEvent = async (calendarEventID) => {
    try {
      const { data: { data: resultData, error } = {} } =
        await CalendarEventServices.deleteCalendarEvent(
          calendarEventID,
          user.loginID
        );

      showAlert(
        resultData ? "success" : "warning",
        resultData
          ? "Calendar Event have been deleted successfully..."
          : error || "An error occurred while deleting the Calendar Event."
      );

      if (resultData) {
        const filtered = await fetchEventCalendarList(fromDate, toDate);
        setFilteredEvents(filtered);
        setSelectedCalendarID(null);
      }
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        "An error occurred while deleting the Event Game details..."
      );
    }
  };

  const fetchPlayerListByEventID = async (EventId) => {
    try {
      const { data } =
        await PlayerProfileService.getPlayerListPageByEventIdYear(
          EventId,
          user?.loginID
        );
      return data;
    } catch (error) {
      console.error(`Error fetching :`, error);
    }
  };

  const saveOrUpdateCalendarEventDetails = async (payload, isUpdate) => {
    try {
      setLoading(true);
      const { data: { data: resultData, error } = {} } =
        await CalendarEventServices.addUpdateCalendarEvent(payload);

      const message = resultData
        ? `Event ${isUpdate ? "updated" : "added"} successfully...`
        : error ||
          `An error occurred while ${
            isUpdate ? "updating" : "adding"
          } the event...`;

      showAlert(resultData ? "success" : "warning", message);

      if (resultData) {
        const filtered = await fetchEventCalendarList(fromDate, toDate);
        setFilteredEvents(filtered);
        resetDependecies();
        resetSelectionOfEvent();
        setIsPanelOpen(false);
      }
    } catch (err) {
      console.log(err);
      showAlert(
        "error",
        `An error occurred while ${
          isUpdate ? "updating" : "adding"
        } the event...`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={loading} />
      <SearchFilter
        fromDate={fromDate}
        toDate={toDate}
        handleDateRangeChange={handleDateRangeChange}
        handleGoButtonClick={handleGoButtonClick}
        classes={classes}
      />

      <Container maxWidth={false} sx={{ pb: "10px" }}>
        {showCalendar && (
          <Paper sx={{ mt: 3, p: "10px" }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={filteredEvents.map((event, index) => {
                return {
                  id: event.eventId.toString(),
                  title: event.title,
                  // start: moment(event.eventStartDateTime).format(
                  //   "YYYY-MM-DD[T]HH:mm:ss"
                  // ),
                  start: `${event.eventStartDateTime}`,
                  end: `${event.eventEndDateTime || event.eventStartDateTime}`,
                  description: event.description,
                  location: event.location,
                  participants: event.participants,
                  color: event.color,
                  extendedProps: {
                    icon: event.icon,
                    color: event.color,
                  },
                };
              })}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              contentHeight="auto" // Automatically adjust height
              // contentHeight="40vh"
              eventContent={(eventInfo) => (
                <EventWithPopover eventInfo={eventInfo} />
              )}
              dayHeaderContent={(args) => (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    height: "35px",
                    alignItems: "center",
                  }}
                >
                  {args.text}
                </span>
              )}
              datesSet={handleDatesSet} // Sync date pickers with the current month when calendar view changes
            />
          </Paper>
        )}

        <CalendarEventHeaderBar
          classes={classes}
          setIsPanelOpen={setIsPanelOpen}
          handleExpand={handleExpand}
          expandedAccordions={expandedAccordions}
          filteredEvents={filteredEvents}
        />

        {/* Accordion List for Events */}
        <CalendarEventListing
          filteredEvents={filteredEvents}
          classes={classes}
          selectedEvent={selectedEvent}
          expandedAccordions={expandedAccordions}
          editEventCalendarDetails={editEventCalendarDetails}
          executeEventDeleteAction={executeEventDeleteAction}
          handleAccordionChange={handleAccordionChange}
          listRef={listRef}
          playerData={playerData}
          participantsOpen={participantsOpen}
          setParticipantsOpen={setParticipantsOpen}
          participantList={participantList}
          participantsCollapseChange={participantsCollapseChange}
          viewPlayerProfile={viewPlayerProfile}
        />
      </Container>

      <AlertSnackbar
        open={alert.open}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ConfirmationDialog
        open={openDeleteEventDialog}
        message="Are you sure you want to delete?"
        handleClose={() => setOpenDeleteEventDialog(false)}
        handleAgree={onDeleteEventAgree}
        handleDisAgree={() => setOpenDeleteEventDialog(false)}
        maxWidth="sm"
      />

      <SlidingPaneComponent
        isOpen={isPanelOpen}
        title={`${!editMode ? "Add Event" : "Update Event"}`}
        onRequestClose={onPanelClose}
      >
        <EventForm
          eventDetails={eventDetails}
          handleChange={handleChange}
          playerData={playerData}
          participants={participants}
          activeView={activeView}
          showParticipants={showParticipants}
          toggleParticipants={toggleParticipants}
          handleToggle={handleToggle}
          addParticipant={addParticipant}
          searchPlayerOrCollegeName={searchPlayerOrCollegeName}
          playerNameSearchText={playerNameSearchText}
          handleOnSaveAction={handleOnSaveAction}
          editMode={editMode}
          commonEventList={commonEventList}
          eventRosterID={eventRosterID}
          setEventRosterID={setEventRosterID}
          loading={loading}
          setParticipants={setParticipants}
          classes={classes}
          handleDateChange={handleDateChange}
          handleEndDateChange={handleEndDateChange}
          handleSelectAllParticipant={handleSelectAllParticipant}
        />
      </SlidingPaneComponent>
    </>
  );
};

export default EventScheduleContainer;
